import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Column, DataTable } from '../../../../components/PrimereactComponents';
import { ButtonAddRounded, ButtonDelete, ButtonEdit } from '../../../components/Buttons';
import { Permission, UserRole } from '../../../constants/enums';
import { hasPermission, hasRole } from '../../../utils/auth';
import { PaginatedDataTable } from '../../../components/PaginatedDataTable';
import { PrimeIcons } from 'primereact/api';
import { BaseChip } from '../../../components/Chips';

export const MunicipalityTable = ({
    municipalities,
    loading,
    user,
    onRowClick,
    onClickDeleteMunicipality,
    onClickAddRequest,
    onClickAddBody,
    onClickEditBody,
    onClickDeleteBody,
    disabled
}) => {
    const { municipalityId } = useParams();
    const [expandedRows, setExpandedRows] = useState(null);

    const navigate = useNavigate();

    // Used to toggle automatic row expansion on page load
    /*
    useEffect(() => {
        if (municipalities) {
            const allRowsExpanded = municipalities.reduce((acc, municipality) => {
                acc[municipality.id] = true;
                return acc;
            }, {});
            setExpandedRows(allRowsExpanded);
        }
    }, [municipalities]);
    */

    const highlightRow = (rowData) => {
        return {
            'table-row--primary': municipalityId && rowData.text_id === municipalityId,
            'table-row--clickable': hasPermission(user, Permission.MANAGE_MUNICIPALITY, rowData.text_id),
            'table-row--bold': hasPermission(user, Permission.MANAGE_MUNICIPALITY, rowData.text_id)
        };
    };

    const highlightExpandedRow = (rowData) => {
        return {
            'table-row--nested': true,
            'table-row--clickable': hasPermission(user, Permission.MANAGE_MUNICIPALITY, rowData.municipality_text_id)
        };
    };

    const partnerTemplate = (rowData) => {
        return rowData.partner ? <i className={`${PrimeIcons.CHECK} color-success`} /> : '';
    };

    const canManageTemplate = (rowData) => {
        return hasPermission(user, Permission.MANAGE_MUNICIPALITY, rowData.text_id) ? (
            <BaseChip label='Ano' severity='success' />
        ) : (
            <BaseChip
                label='Ne'
                hoverable
                title='Požádat o právo spravovat municipalitu'
                onClick={() => onClickAddRequest(rowData)}
            />
        );
    };

    const municipalityButtonsTemplate = (rowData) => {
        return (
            <div className='table-buttons'>
                {hasRole(user, UserRole.ADMIN) && (
                    <ButtonAddRounded
                        rounded
                        title='Přidat orgán'
                        disabled={disabled}
                        onClick={() => onClickAddBody(rowData)}
                    />
                )}

                {hasPermission(user, Permission.MANAGE_MUNICIPALITY, rowData.text_id) && (
                    <ButtonEdit
                        rounded
                        title='Editovat municipalitu'
                        disabled={disabled}
                        onClick={() => navigate(`/admin/municipality/${rowData.text_id}/edit`)}
                    />
                )}
                {hasRole(user, UserRole.ADMIN) && (
                    <ButtonDelete
                        rounded
                        title='Smazat municipalitu'
                        disabled={disabled}
                        onClick={() => onClickDeleteMunicipality(rowData)}
                    />
                )}
            </div>
        );
    };

    const bodyButtonsTemplate = (rowData) => {
        return (
            <div className='table-buttons'>
                {hasPermission(user, Permission.MANAGE_MUNICIPALITY, rowData.municipality_text_id) && (
                    <ButtonEdit
                        rounded
                        title='Editovat orgán'
                        disabled={disabled}
                        onClick={() => onClickEditBody(rowData)}
                    />
                )}
                {hasRole(user, UserRole.ADMIN) && (
                    <ButtonDelete
                        rounded
                        title='Smazat orgán'
                        disabled={disabled}
                        onClick={() => onClickDeleteBody(rowData)}
                    />
                )}
            </div>
        );
    };

    const rowExpansionTemplate = (data) => {
        return (
            <DataTable className='table--nested'
                value={data.bodies}
                emptyMessage='Nebyly nalezeny žádné orgány'
                onRowClick={handleExpandedRowClick}
                rowHover
                rowClassName={highlightExpandedRow}
            >
                <Column field='name' header='Název orgánu' />
                <Column body={bodyButtonsTemplate} align='right' />
            </DataTable>
        );
    };

    const handleRowClick = (event) => {
        const rowData = event.data;
        const newExpandedRows = { ...expandedRows };

        if (expandedRows && expandedRows[rowData.id]) {
            delete newExpandedRows[rowData.id];
        } else {
            newExpandedRows[rowData.id] = true;
        }

        setExpandedRows(newExpandedRows);
    };

    const handleExpandedRowClick = (event) => {
        if (hasPermission(user, Permission.MANAGE_MUNICIPALITY, event.data.municipality_text_id)) {
            navigate(`/admin/municipality/${event.data.municipality_text_id}/${event.data.type_text_id}/funkcni-obdobi`);
        }
    };

    return (
        <PaginatedDataTable
            value={municipalities}
            loading={loading}
            removableSort
            initialRows={25}
            breakpoint='900px'
            emptyMessage='Nebyly nalezeny žádné municipality'
            onRowClick={handleRowClick}
            rowHover
            rowClassName={highlightRow}
            expandedRows={expandedRows}
            onRowToggle={(e) => setExpandedRows(e.data)}
            rowExpansionTemplate={rowExpansionTemplate}
            dataKey='id'
        >
            <Column expander style={{ width: '3em' }} />
            <Column field='name' header='Název' sortable />
            <Column field='type_name' header='Typ' />
            <Column field='text_id' header='Textové ID' sortable />
            <Column field='email' header='Kontaktní email' />
            <Column header='Partner' body={partnerTemplate} align='center' />
            {!hasRole(user, UserRole.ADMIN) && <Column header='Správce' body={canManageTemplate} align='center' />}
            <Column body={municipalityButtonsTemplate} align='right' />
        </PaginatedDataTable>
    );
};
